/**
 * PKUPathway.jsx
 *
 * Dependencies — install before use:
 *   npm install gsap
 *
 * SmilesDrawer is loaded from CDN via a <script> tag injected on mount.
 *
 * Usage:
 *   import PKUPathway from './PKUPathway';
 *   <PKUPathway />
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SmilesDrawer from 'smiles-drawer/src/SmilesDrawer';
import ContactFooter from '../components/ContactFooter';

gsap.registerPlugin(ScrollTrigger);

// ─── FONTS ────────────────────────────────────────────────────────────────────
const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=IBM+Plex+Mono:wght@400;500&family=Syne:wght@400;600&display=swap';

// ─── STEP DATA ─────────────────────────────────────────────────────────────────
const STEPS = [
  {
    num: '01 / 05',
    title: 'The Pathway',
    body: 'In healthy metabolism, phenylalanine from dietary protein is converted to tyrosine by PAH enzyme. Tyrosine then feeds dopamine, melanin, and thyroid hormone synthesis.',
    badge: 'Normal metabolism',
    ok: true,
    show: ['n-phe', 'n-pah', 'n-tyr', 'n-down', 'e1', 'e2', 'e3'],
    hide: ['n-pah-b', 'e2b', 'e3d', 'n-acc', 'n-brain', 'badge-phe', 'e-tox'],
    tyrStroke: 'rgba(0,229,176,0.5)',
    pheGlow: false,
  },
  {
    num: '02 / 05',
    title: 'PAH is Mutated',
    body: 'In PKU, both copies of the PAH gene carry loss-of-function mutations (autosomal recessive). The enzyme cannot perform the hydroxylation reaction.',
    badge: 'Enzyme deficiency',
    ok: false,
    show: ['n-phe', 'n-pah-b', 'n-tyr', 'e1', 'e2b'],
    hide: ['n-pah', 'e2', 'e3', 'e3d', 'n-acc', 'n-brain', 'badge-phe', 'e-tox', 'n-down'],
    tyrStroke: 'rgba(58,66,85,0.4)',
    pheGlow: false,
  },
  {
    num: '03 / 05',
    title: 'Phenylalanine Accumulates',
    body: 'Without PAH activity, blood phenylalanine rises to 20× normal (>1200 μmol/L). Tyrosine is depleted, starving every downstream pathway.',
    badge: 'Hyperphenylalaninemia',
    ok: false,
    show: ['n-phe', 'n-pah-b', 'n-tyr', 'e1', 'e2b', 'n-acc', 'badge-phe', 'e3d'],
    hide: ['n-pah', 'e2', 'e3', 'n-brain', 'e-tox', 'n-down'],
    tyrStroke: 'rgba(58,66,85,0.35)',
    pheGlow: true,
  },
  {
    num: '04 / 05',
    title: 'Brain Toxicity',
    body: 'Excess phenylalanine floods the blood-brain barrier, disrupts myelination and depletes serotonin and dopamine. Untreated in infants, this causes severe intellectual disability.',
    badge: 'Neurological impact',
    ok: false,
    show: ['n-phe', 'n-pah-b', 'n-acc', 'badge-phe', 'e-tox', 'n-brain'],
    hide: ['n-pah', 'e2', 'e2b', 'e1', 'e3', 'e3d', 'n-tyr', 'n-down'],
    tyrStroke: 'rgba(58,66,85,0.35)',
    pheGlow: true,
  },
  {
    num: '05 / 05',
    title: 'Treatment',
    body: '· Phe-restricted diet limits substrate intake\n· Sapropterin (BH4) restores activity in ~40% of patients\n· Pegvaliase: enzyme substitution (FDA 2018)\n· Gene therapy: active clinical trials',
    badge: 'Manageable with early detection',
    ok: true,
    show: ['n-phe', 'n-pah', 'n-tyr', 'n-down', 'e1', 'e2', 'e3'],
    hide: ['n-pah-b', 'e2b', 'e3d', 'n-acc', 'n-brain', 'badge-phe', 'e-tox'],
    tyrStroke: 'rgba(0,229,176,0.5)',
    pheGlow: false,
  },
];

// ─── MOLECULE DATA ─────────────────────────────────────────────────────────────
const MOLS = {
  aasa:      { name: 'AASA', smiles: 'O=C(O)C(N)CCCC=O',                         desc: 'Essential amino acid from diet. Accumulates to toxic levels in PKU when PAH is non-functional.' },
  phe:      { name: 'Phenylalanine', smiles: 'N[C@@H](Cc1ccccc1)C(=O)O',                         desc: 'Essential amino acid from diet. Accumulates to toxic levels in PKU when PAH is non-functional.' },
  tyr:      { name: 'Tyrosine',      smiles: 'N[C@@H](Cc1ccc(O)cc1)C(=O)O',                      desc: 'Produced by PAH from phenylalanine. Depleted in PKU; precursor to dopamine, melanin, and thyroid hormones.' },
  dopa:     { name: 'L-DOPA',        smiles: 'N[C@@H](Cc1ccc(O)c(O)c1)C(=O)O',                   desc: 'Intermediate in dopamine synthesis from tyrosine. Also the basis of Parkinson\'s treatment.' },
  dopamine: { name: 'Dopamine',      smiles: 'NCCc1ccc(O)c(O)c1',                                 desc: 'Critical neurotransmitter. Synthesis is impaired in PKU due to tyrosine depletion.' },
  bh4:      { name: 'BH4 Cofactor',  smiles: 'OC1Nc2nc(N)[nH]c(=O)c2N[C@@H]1[C@H](O)[C@H](O)C', desc: 'Required cofactor for PAH. Sapropterin (synthetic BH4) restores PAH function in ~40% of patients.' },
};

// ─── STYLES ───────────────────────────────────────────────────────────────────
const styles = {
  root: {
    background: '#07080a',
    color: '#dde3f0',
    fontFamily: "'Syne', sans-serif",
    overflowX: 'hidden',
  },
  // Hero
  hero: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '2rem',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    position: 'relative',
  },
  heroTag: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.65rem',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#00e5b0',
    marginBottom: '1.5rem',
    opacity: 0,
  },
  heroH1: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(3.5rem, 9vw, 8rem)',
    lineHeight: 0.95,
    letterSpacing: '-0.02em',
    marginBottom: '1.5rem',
    opacity: 0,
  },
  heroP: {
    maxWidth: '440px',
    color: '#5a637a',
    fontSize: '0.95rem',
    lineHeight: 1.7,
    fontFamily: "'IBM Plex Mono', monospace",
    opacity: 0,
  },
  scrollHint: {
    position: 'absolute',
    bottom: '1rem',
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.2em',
    color: '#5a637a',
    textTransform: 'uppercase',
    opacity: 0,
    pointerEvents: 'none',
  },
  // Pathway
  pathwaySection: {
    // height: `${STEPS.length * 100}vh`,
    position: 'relative',
  },
  pathwaySticky: {
    position: 'relative',
    // top: 0,
    height: '100vh',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'center',
    padding: '0 6vw',
    gap: '4rem',
    overflow: 'hidden',
    
  },
  mitoLayer: (active, img) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',

  backgroundImage: `url(${img})`,
  backgroundSize: '900px auto',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',

  opacity: active ? 0.35 : 0,
  // opacity: .35,
  transition: 'opacity 0.6s ease',

  filter: 'blur(6px) saturate(1.2)',
}),
  diagramWrap: {
    position: 'relative',
  zIndex: 1,
  },
  // Step panel
  stepPanel: { position: 'relative',
  zIndex: 1,},
  stepNum: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.2em',
    color: '#5a637a',
    textTransform: 'uppercase',
    marginBottom: '0.6rem',
  },
  stepTitle: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(1.8rem, 3vw, 2.8rem)',
    lineHeight: 1.1,
    marginBottom: '1rem',
  },
  stepBody: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.8rem',
    lineHeight: 1.8,
    color: '#5a637a',
    whiteSpace: 'pre-line',
  },
  // Viewer
  viewerSection: {
    padding: '8rem 6vw',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    background: '#0f1117',
  },
  viewerLabel: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.2em',
    color: '#00e5b0',
    textTransform: 'uppercase',
    marginBottom: '0.8rem',
  },
  viewerHeading: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(2rem, 4vw, 3.5rem)',
    marginBottom: '3rem',
  },
  viewerTabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2.5rem',
    flexWrap: 'wrap',
  },
  viewerGrid: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: '3rem',
    alignItems: 'start',
    maxWidth: '860px',
  },
  canvasCard: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.07)',
    padding: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '220px',
  },
  molName: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: '1.8rem',
    marginBottom: '0.5rem',
  },
  molSmiles: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.65rem',
    color: '#00e5b0',
    background: 'rgba(0,229,176,0.05)',
    border: '1px solid rgba(0,229,176,0.12)',
    padding: '0.5rem 0.7rem',
    margin: '0.7rem 0',
    wordBreak: 'break-all',
    display: 'block',
  },
  molDesc: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.75rem',
    color: '#5a637a',
    lineHeight: 1.75,
  },
  heroVisual: {
  position: 'relative',
  marginTop: '3rem',
  marginBottom: '2rem',
  height: '280px',
},

baby: {
  width: '220px',
  height: '220px',
  objectFit: 'contain',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  background: 'transparent',
  mixBlendMode: 'lighten',
  filter: 'drop-shadow(0 0 20px rgba(255,60,78,0.3))',
},

dot: {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  background: '#00e5b0',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%) scale(0)',
  opacity: 0,
},
};

const mitoImg = '/mitochondrion.png';
const babyImg = '/baby_transparent.png';

function badgeStyle(ok) {
  return {
    display: 'inline-block',
    marginTop: '1.2rem',
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '0.3rem 0.8rem',
    borderRadius: '2px',
    background: ok ? 'rgba(0,229,176,0.1)' : 'rgba(255,60,78,0.1)',
    color:      ok ? '#00e5b0'              : '#ff3c4e',
    border:     ok ? '1px solid rgba(0,229,176,0.3)' : '1px solid rgba(255,60,78,0.3)',
  };
}

function tabStyle(active) {
  return {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.7rem',
    padding: '0.4rem 1rem',
    border: active ? '1px solid #00e5b0' : '1px solid rgba(255,255,255,0.07)',
    background: active ? 'rgba(0,229,176,0.06)' : 'transparent',
    color: active ? '#00e5b0' : '#5a637a',
    cursor: 'pointer',
    letterSpacing: '0.05em',
    transition: 'all 0.25s',
  };
}

// ─── PATHWAY SVG ───────────────────────────────────────────────────────────────
// All visibility is driven by the `vis` prop (object of id -> 0|1)
// and tyrStroke / pheGlow for the dynamic overrides.
function PathwaySVG({ vis, tyrStroke, pheGlow }) {
  const o = (id) => vis[id] ?? 0;

  return (
    <svg
      viewBox="0 0 420 480"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', maxWidth: '480px', height: 'auto', overflow: 'visible' }}
    >
      <defs>
        <marker id="a-teal" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill="rgba(0,229,176,0.85)" />
        </marker>
        <marker id="a-red" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill="rgba(255,60,78,0.85)" />
        </marker>
        <marker id="a-dim" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill="rgba(58,66,85,0.7)" />
        </marker>
        <filter id="glow-t">
          <feGaussianBlur stdDeviation="5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-r">
          <feGaussianBlur stdDeviation="8" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Edges */}
      <line id="e1"   opacity={o('e1')}   x1="105" y1="140" x2="168" y2="140" stroke="rgba(0,229,176,0.7)"  strokeWidth="2"   markerEnd="url(#a-teal)" style={{transition:'opacity 0.5s'}}/>
      <line id="e2"   opacity={o('e2')}   x1="252" y1="140" x2="310" y2="140" stroke="rgba(0,229,176,0.7)"  strokeWidth="2"   markerEnd="url(#a-teal)" style={{transition:'opacity 0.5s'}}/>
      <line id="e2b"  opacity={o('e2b')}  x1="252" y1="140" x2="310" y2="140" stroke="rgba(255,60,78,0.8)"  strokeWidth="2"   strokeDasharray="6 4" markerEnd="url(#a-red)" style={{transition:'opacity 0.5s'}}/>
      <line id="e3"   opacity={o('e3')}   x1="355" y1="168" x2="355" y2="240" stroke="rgba(0,229,176,0.5)"  strokeWidth="1.5" markerEnd="url(#a-teal)" style={{transition:'opacity 0.5s'}}/>
      <line id="e3d"  opacity={o('e3d')}  x1="355" y1="168" x2="355" y2="240" stroke="rgba(58,66,85,0.5)"   strokeWidth="1.5" strokeDasharray="4 4" markerEnd="url(#a-dim)" style={{transition:'opacity 0.5s'}}/>
      <path id="e-tox" opacity={o('e-tox')} d="M 60 108 Q 60 50 200 50 Q 310 50 330 80" stroke="rgba(255,60,78,0.75)" strokeWidth="2" strokeDasharray="6 3" markerEnd="url(#a-red)" style={{transition:'opacity 0.5s'}}/>

      {/* PHE node */}
      <g id="n-phe" opacity={o('n-phe')} filter={pheGlow ? 'url(#glow-r)' : undefined} style={{transition:'opacity 0.5s'}}>
        <circle cx="60" cy="140" r="58" fill="#0a0c10" stroke="rgba(0,229,176,0.5)" strokeWidth="1.5" />
        <polygon points="60,112 80,123 80,147 60,158 40,147 40,123" fill="none" stroke="rgba(0,229,176,0.6)" strokeWidth="1.5" />
        <circle cx="60" cy="135" r="9" fill="none" stroke="rgba(0,229,176,0.25)" strokeWidth="1" strokeDasharray="3 2" />
        <line x1="60" y1="158" x2="60" y2="168" stroke="rgba(90,99,122,0.6)" strokeWidth="1.2" />
        <text x="60" y="175" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill="rgba(90,99,122,0.9)">CH₂·CH(NH₂)·COOH</text>
        <text x="60" y="210" textAnchor="middle" fontFamily="Syne" fontSize="11" fill="#dde3f0">Phenylalanine</text>
        <text x="60" y="223" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8"  fill="rgba(90,99,122,0.7)">C₉H₁₁NO₂</text>
      </g>

      {/* PAH normal */}
      <g id="n-pah" opacity={o('n-pah')} style={{transition:'opacity 0.5s'}}>
        <rect x="168" y="120" width="84" height="40" rx="4" fill="rgba(0,229,176,0.06)" stroke="rgba(0,229,176,0.45)" strokeWidth="1.2" />
        <text x="210" y="137" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9"   fill="rgba(0,229,176,0.9)">PAH</text>
        <text x="210" y="152" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill="rgba(90,99,122,0.8)">enzyme</text>
      </g>

      {/* PAH broken */}
      <g id="n-pah-b" opacity={o('n-pah-b')} style={{transition:'opacity 0.5s'}}>
        <rect x="168" y="120" width="84" height="40" rx="4" fill="rgba(255,60,78,0.1)" stroke="rgba(255,60,78,0.7)" strokeWidth="1.5" />
        <text x="210" y="137" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9"   fill="rgba(255,60,78,0.9)">PAH ✕</text>
        <text x="210" y="152" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill="rgba(255,60,78,0.6)">mutated</text>
      </g>

      {/* TYR node */}
      <g id="n-tyr" opacity={o('n-tyr')} style={{transition:'opacity 0.5s'}}>
        <circle id="c-tyr" cx="355" cy="140" r="48" fill="#0a0c10" stroke={tyrStroke} strokeWidth="1.5" style={{transition:'stroke 0.5s'}} />
        <polygon points="355,117 370,126 370,144 355,153 340,144 340,126" fill="none" stroke="rgba(0,229,176,0.55)" strokeWidth="1.4" />
        <line x1="355" y1="117" x2="355" y2="107" stroke="rgba(245,166,35,0.65)" strokeWidth="1.2" />
        <text x="355" y="103" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill="rgba(245,166,35,0.85)">OH</text>
        <text x="355" y="172" textAnchor="middle" fontFamily="Syne"          fontSize="11" fill="#dde3f0">Tyrosine</text>
        <text x="355" y="185" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8"  fill="rgba(90,99,122,0.7)">C₉H₁₁NO₃</text>
      </g>

      {/* Downstream */}
      <g id="n-down" opacity={o('n-down')} style={{transition:'opacity 0.5s'}}>
        <circle cx="355" cy="290" r="36" fill="#0a0c10" stroke="rgba(58,66,85,0.5)" strokeWidth="1.2" />
        <text x="355" y="287" textAnchor="middle" fontFamily="Syne" fontSize="9" fill="rgba(90,99,122,0.8)">Dopamine</text>
        <text x="355" y="300" textAnchor="middle" fontFamily="Syne" fontSize="9" fill="rgba(90,99,122,0.8)">Melanin · T3/T4</text>
      </g>

      {/* Accumulation rings */}
      <g id="n-acc" opacity={o('n-acc')} style={{transition:'opacity 0.5s'}}>
        <circle cx="60" cy="140" r="72" fill="none" stroke="rgba(255,60,78,0.15)" strokeWidth="2" strokeDasharray="5 4" />
        <circle cx="60" cy="140" r="86" fill="none" stroke="rgba(255,60,78,0.07)" strokeWidth="2" strokeDasharray="3 6" />
        <text x="60" y="248" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8.5" fill="rgba(255,60,78,0.8)" letterSpacing="0.08em">↑ TOXIC BUILDUP</text>
      </g>

      {/* Brain */}
      <g id="n-brain" opacity={o('n-brain')} style={{transition:'opacity 0.5s'}}>
        <circle cx="330" cy="78" r="30" fill="rgba(255,60,78,0.1)" stroke="rgba(255,60,78,0.6)" strokeWidth="1.5" />
        <text x="330" y="75" textAnchor="middle" fontFamily="Syne"          fontSize="9"   fill="rgba(255,60,78,0.9)">Brain</text>
        <text x="330" y="88" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill="rgba(255,60,78,0.7)">toxicity</text>
      </g>

      {/* PHE badge */}
      <g id="badge-phe" opacity={o('badge-phe')} style={{transition:'opacity 0.5s'}}>
        <rect x="8" y="64" width="78" height="18" rx="9" fill="rgba(255,60,78,0.85)" />
        <text x="47" y="77" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill="white">↑ 20× normal</text>
      </g>
    </svg>
  );
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function RareDisease() {
  // Step state — drives SVG + panel text
  const babyRef = useRef(null);
  const dotRef = useRef(null);
  const mitoRef = useRef(null);
  const [stepIdx, setStepIdx]   = useState(0);
  const [activeMol, setActiveMol] = useState('phe');

  // Refs for GSAP targets
  const heroTagRef   = useRef(null);
  const heroH1Ref    = useRef(null);
  const heroPRef     = useRef(null);
  const hintRef      = useRef(null);
  const numRef       = useRef(null);
  const titleRef     = useRef(null);
  const bodyRef      = useRef(null);
  const badgeRef     = useRef(null);
  const pathwayRef   = useRef(null);
  const canvasRef    = useRef(null);
  const drawerRef    = useRef(null);

  const currentStep = STEPS[stepIdx];

  // Build vis map from current step
  const vis = {};
  currentStep.show.forEach((id) => { vis[id] = 1; });
  currentStep.hide.forEach((id) => { vis[id] = 0; });

  // ── Load fonts
  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
      const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: FONT_URL });
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
  drawerRef.current = new SmilesDrawer({
    width: 240,
    height: 200,
    themes: {
      dark: {
        C: '#c8d4f0',
        O: '#f28b82',
        N: '#8ab4f8',
        H: '#5a637a',
        S: '#f9e26e',
        BACKGROUND: 'transparent',
      },
    },
  });
}, []);

useEffect(() => {
  if (!drawerRef.current) return;

  const mol = MOLS[activeMol];

  drawerRef.current.draw(
    mol.smiles,
    "#smiles-canvas",
    "dark",
    false
  );
}, [activeMol]);

  // ── Hero entrance animation
  useEffect(() => {
    const els = [heroTagRef.current, heroH1Ref.current, heroPRef.current];
    gsap.set(els, { y: 24, opacity: 0 });
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    tl.to(heroTagRef.current, { opacity: 1, y: 0, duration: 0.8, delay: 0.3 })
      .to(heroH1Ref.current,  { opacity: 1, y: 0, duration: 1   }, '-=0.4')
      .to(heroPRef.current,   { opacity: 1, y: 0, duration: 0.8 }, '-=0.5')
      .to(hintRef.current,    { opacity: 1,       duration: 0.6 }, '-=0.2');
    return () => tl.kill();
  }, []);

  // ── Animate panel text when step changes (skip initial render)
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    const targets = [numRef.current, titleRef.current, bodyRef.current, badgeRef.current];
    const tl = gsap.timeline();
    tl.to(targets, { opacity: 0, y: -10, duration: 0.18, stagger: 0.04 })
      .to(targets, { opacity: 1, y: 0,   duration: 0.32, stagger: 0.06 });
    return () => tl.kill();
  }, [stepIdx]);

  // ── ScrollTrigger for pathway steps
//   useEffect(() => {
//     if (!pathwayRef.current) return;
//     const triggers = STEPS.map((_, i) =>
//       ScrollTrigger.create({
//         trigger: pathwayRef.current,
//         start: `${i * 100}% top`,
// end: `${(i + 1) * 100}% top`,
//         onEnter:     () => setStepIdx(i),
//         onEnterBack: () => setStepIdx(i),
//       })
//     );
//     return () => triggers.forEach((t) => t.kill());
//   }, []);

useEffect(() => {
  if (!pathwayRef.current) return;

  const totalSteps = STEPS.length;

  const trigger = ScrollTrigger.create({
    trigger: pathwayRef.current,

    start: 'top top',

    // 👇 this replaces your fake 600vh
    end: `+=${window.innerHeight * (totalSteps - 1)}`,

    pin: true,           // 🔥 THIS is the key
    scrub: 0.6,

    snap: {
      snapTo: 1 / (totalSteps - 1),
      duration: 0.3,
      ease: 'power2.out',
    },

    onUpdate: (self) => {
      const idx = Math.round(self.progress * (totalSteps - 1));
      setStepIdx((prev) => (prev !== idx ? idx : prev));
    },

    // markers: true,
  });

  return () => trigger.kill();
}, []);

useEffect(() => {
  if (!mitoRef.current) return;

  if (stepIdx === 1) {
    gsap.fromTo(
      mitoRef.current,
      { opacity: 0, scale: 0.95 },
      { opacity: 1, scale: 1, duration: 0.6, ease: 'power2.out' }
    );
  } else {
    gsap.to(mitoRef.current, {
      opacity: 0,
      duration: 0.4,
      ease: 'power2.inOut',
    });
  }
}, [stepIdx]);

// Animate baby → dot transition on step 2
useEffect(() => {
  if (!babyRef.current || !dotRef.current) return;

  gsap.set(dotRef.current, {
    scale: 0,
    opacity: 0,
  });

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: babyRef.current,
      start: 'top 60%',
      end: '+=300',

      // play on scroll down
      // reverse on scroll up
      toggleActions: 'play reverse play reverse',

      // optional for smoother syncing
      scrub: 1,
    },
  });

  tl.to(babyRef.current, {
    scale: 0.2,
    opacity: 0,
    duration: 0.8,
    ease: 'power2.inOut',
  })
  .to(
    dotRef.current,
    {
      scale: 1,
      opacity: 1,
      duration: 0.6,
      ease: 'power2.out',
    },
    '-=0.4'
  );

  return () => {
    tl.scrollTrigger?.kill();
    tl.kill();
  };
}, []);

// useEffect(() => {
//   console.log('stepIdx:', stepIdx);
// }, [stepIdx]);

  return (
    <div style={styles.root}>

      {/* HERO */}
      <section style={styles.hero}>
        <div ref={heroTagRef} style={styles.heroTag}>Molecular Pathway · PKU</div>
        <h1 ref={heroH1Ref} style={styles.heroH1}>
          One broken<br />enzyme.<br />
          <em style={{ color: '#ff3c4e', fontStyle: 'italic' }}>Everything</em><br />
          changes.
        </h1>
        <p ref={heroPRef} style={styles.heroP}>
          Phenylketonuria is caused by a single non-functional enzyme — PAH. Scroll to see how.
        </p>
        <div ref={hintRef} style={styles.scrollHint}>↓ scroll to explore</div>

        <div style={styles.heroVisual}>
          <img src={babyImg} ref={babyRef} style={styles.baby} />
          <div ref={dotRef} style={styles.dot} />
        </div>
      
      </section>

      {/* PATHWAY */}
      <section ref={pathwayRef} style={styles.pathwaySection}>
        <div style={styles.pathwaySticky}>

          <div style={styles.mitoLayer(stepIdx === 1, mitoImg)} />

          {/* SVG diagram — purely declarative, no DOM refs needed */}
          <div style={styles.diagramWrap}>
            <PathwaySVG
              vis={vis}
              tyrStroke={currentStep.tyrStroke}
              pheGlow={currentStep.pheGlow}
            />
          </div>

          {/* Step text panel */}
          <div style={styles.stepPanel}>
            <div ref={numRef}   style={styles.stepNum}>{currentStep.num}</div>
            <div ref={titleRef} style={styles.stepTitle}>{currentStep.title}</div>
            <div ref={bodyRef}  style={styles.stepBody}>{currentStep.body}</div>
            <span ref={badgeRef} style={badgeStyle(currentStep.ok)}>{currentStep.badge}</span>
          </div>

        </div>
      </section>

      {/* MOLECULE VIEWER */}
      <section style={styles.viewerSection}>
        <div style={styles.viewerLabel}>Interactive Explorer</div>
        <div style={styles.viewerHeading}>Molecule Structures</div>

        <div style={styles.viewerTabs}>
          {Object.keys(MOLS).map((key) => (
            <button
              key={key}
              style={tabStyle(activeMol === key)}
              onClick={() => setActiveMol(key)}
            >
              {MOLS[key].name}
            </button>
          ))}
        </div>

        <div style={styles.viewerGrid}>
          <div style={styles.canvasCard}>
            <canvas ref={canvasRef} id="smiles-canvas" width={240} height={200} style={{ display: 'block', maxWidth: '100%' }} />
          </div>
          <div>
            <div style={styles.molName}>{MOLS[activeMol].name}</div>
            <code style={styles.molSmiles}>{MOLS[activeMol].smiles}</code>
            <p style={styles.molDesc}>{MOLS[activeMol].desc}</p>
          </div>
        </div>
      </section>

      {/* HOW YOU CAN HELP */}
      <section style={{ padding: '7rem 6vw 6rem', borderTop: '1px solid rgba(0,229,176,0.16)', background: 'linear-gradient(to bottom, #07080a 0%, #0a100e 100%)' }}>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.2em', color: '#00e5b0', textTransform: 'uppercase', marginBottom: '0.8rem' }}>Get Involved</div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', marginBottom: '1.4rem' }}>How You Can Help</div>
        <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.78rem', color: '#5a637a', lineHeight: 1.85, maxWidth: '620px', marginBottom: '3rem' }}>
          A disease being rare does not make it unsolvable &mdash; it makes it under-resourced. Patients, families, and the foundations they build are what turn rare conditions into treatable ones.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.6rem', maxWidth: '820px', marginBottom: '3.5rem' }}>
          {[
            { t: 'Give', d: 'Fund research and direct family support. Small recurring gifts compound into trials.' },
            { t: 'Join a registry', d: 'Patient data is what makes clinical trials possible and accelerates the next therapy.' },
            { t: 'Amplify', d: 'Share these stories. Awareness drives the funding and policy that follow.' },
          ].map((w) => (
            <div key={w.t} style={{ borderTop: '2px solid #00e5b0', paddingTop: '1rem' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.5rem' }}>{w.t}</div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.72rem', color: '#5a637a', lineHeight: 1.8 }}>{w.d}</div>
            </div>
          ))}
        </div>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.2em', color: '#5a637a', textTransform: 'uppercase', marginBottom: '1.1rem' }}>Foundations Worth Supporting</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '820px' }}>
          {[
            { name: 'National Organization for Rare Disorders (NORD)', desc: 'Patient assistance programs, advocacy, and research grants.', url: 'https://rarediseases.org' },
            { name: 'Global Genes', desc: 'Connects, equips, and empowers the rare disease community.', url: 'https://globalgenes.org' },
            { name: 'EveryLife Foundation for Rare Diseases', desc: 'Drives science-based public policy for rare disease patients.', url: 'https://everylifefoundation.org' },
            { name: 'Rare Diseases International', desc: 'The global voice advocating for rare disease patients worldwide.', url: 'https://www.rarediseasesinternational.org' },
          ].map((f) => (
            <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
               style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '1.3rem 1.5rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', textDecoration: 'none', transition: 'border-color 0.18s ease, background 0.18s ease' }}
               onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(0,229,176,0.5)'; e.currentTarget.style.background = 'rgba(0,229,176,0.05)'; }}
               onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
              <div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.9rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.3rem' }}>{f.name}</div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.7rem', color: '#5a637a', lineHeight: 1.65 }}>{f.desc}</div>
              </div>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '1rem', color: '#00e5b0', flexShrink: 0 }}>&rarr;</span>
            </a>
          ))}
        </div>
      </section>

      <ContactFooter />
    </div>
  );
}
